import { useQuery } from "@tanstack/react-query";
import {
  FilterSearch,
  FilterSelect,
  FiltersToolbar,
  PaymentStatusBadge,
  formatDateTimeVn,
  formatVnd,
} from "@chotca/ui";
import type { AdminPlanPurchaseItem } from "@chotca/config";
import { getSdk } from "@chotca/api-client";
import { useTableFilters } from "@/lib/use-table-filters";
import { DataTable, usePagination, type Column } from "@/components/data-table";

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Chờ thanh toán" },
  { value: "PAID", label: "Đã thanh toán" },
  { value: "EXPIRED", label: "Hết hạn" },
  { value: "FAILED", label: "Thất bại" },
];

function startOfDayIso(date: string): string {
  return new Date(`${date}T00:00:00`).toISOString();
}
function endOfDayIso(date: string): string {
  return new Date(`${date}T23:59:59.999`).toISOString();
}

export function PlanPurchasesTab() {
  const filters = useTableFilters({
    search: { debounce: 300 },
    status: {},
    from: {},
    to: {},
  });
  const { page, pageSize, setPage, setPageSize } = usePagination();

  const listQuery = {
    search: filters.applied.search,
    status: filters.applied.status as AdminPlanPurchaseItem["status"],
    from: filters.applied.from
      ? startOfDayIso(filters.applied.from)
      : undefined,
    to: filters.applied.to ? endOfDayIso(filters.applied.to) : undefined,
    page,
    limit: pageSize,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "planPurchases", listQuery],
    queryFn: async () => getSdk().admin.listPlanPurchases(listQuery),
  });

  const list = (data?.items ?? []) as AdminPlanPurchaseItem[];

  const columns: Column<AdminPlanPurchaseItem>[] = [
    {
      key: "order",
      header: "Mã đơn",
      cell: (p) => (
        <span className="font-mono text-xs font-semibold text-ink">
          {p.providerOrderId}
        </span>
      ),
    },
    {
      key: "org",
      header: "Doanh nghiệp",
      cell: (p) => (
        <span className="line-clamp-1 max-w-52 text-sm">{p.orgName}</span>
      ),
    },
    {
      key: "plan",
      header: "Nội dung",
      cell: (p) =>
        p.kind === "PLAN" ? (
          <span className="rounded-full bg-mist px-2 py-0.5 text-[11px] font-bold uppercase">
            {p.planCode}
          </span>
        ) : (
          <span className="text-xs text-ink">
            {p.quantity ?? 0}{" "}
            {p.kind === "SOURCING" ? "lượt săn" : "lượt boost"}
          </span>
        ),
    },
    {
      key: "amount",
      header: "Số tiền",
      align: "right",
      cell: (p) => (
        <span className="font-mono font-semibold">
          {formatVnd(Number(p.amountVnd))}
        </span>
      ),
    },
    {
      key: "status",
      header: "Trạng thái",
      cell: (p) => <PaymentStatusBadge status={p.status} />,
    },
    {
      key: "createdAt",
      header: "Tạo lúc",
      cell: (p) => (
        <span className="text-xs text-ash">
          {formatDateTimeVn(p.createdAt)}
        </span>
      ),
    },
    {
      key: "paidAt",
      header: "Thanh toán lúc",
      cell: (p) => (
        <span className="text-xs text-ash">
          {p.paidAt ? formatDateTimeVn(p.paidAt) : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
      >
        <FilterSearch
          value={filters.values.search ?? ""}
          onChange={(v) => filters.set("search", v)}
          placeholder="Tìm mã đơn hoặc tên doanh nghiệp..."
        />
        <FilterSelect
          value={filters.values.status ?? ""}
          onValueChange={(v) => filters.set("status", v)}
          placeholder="Mọi trạng thái"
          options={STATUS_OPTIONS}
        />
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(p) => p.id}
        emptyText="Chưa có đơn mua gói nào khớp bộ lọc."
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
